import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { Checkbox } from 'us-react-components/dist/Checkbox/Checkbox';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { Button, ButtonType } from '../../../components/Button';
import {
  AvailabilityStatusService,
  CollegeService,
  CollegeAreaOfStudyService,
  JobAvailabilityType,
  JobPreference,
} from 'us-web-services';
import toast from 'react-hot-toast';


interface ModalBase {
  isOpen: boolean;
  onClose: () => void;
}

// NoteModal, flag-content, ConfirmModal, ProfileVideoModal promoted to us-react-components
// (SitterNoteModal, FlagProfileModal). Import them directly from the shared
// library in the SitterProfile container.

// -- Availability Modal (job seeking status) -------------------------------
const STATUS_DESCRIPTION_MAX = 500;

interface RecurringDay {
  dayOfWeek?: { code?: string };
  morning?: boolean;
  afternoon?: boolean;
  night?: boolean;
}

const DEFAULT_RECURRING_DAYS: RecurringDay[] = [
  { dayOfWeek: { code: 'SUN' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'MON' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'TUE' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'WED' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'THU' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'FRI' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'SAT' }, morning: false, afternoon: false, night: false },
];

type RecurringSlot = 'morning' | 'afternoon' | 'night';

const AvailabilityRecurringEditor = ({
  days,
  onToggle,
}: {
  days: RecurringDay[];
  onToggle: (index: number, slot: RecurringSlot) => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const cellButton = (
    on: boolean,
    dayPart: 'sun' | 'moon',
    dayCode: string,
    slot: RecurringSlot,
    index: number,
  ): JSX.Element => (
    <button
      type='button'
      aria-label={t('pages.sitterProfile.modals.jobSeekingStatusModal.toggleCellAriaLabel', {
        slot,
        day: dayCode,
      })}
      onClick={(): void => onToggle(index, slot)}
      className='bg-transparent border-0 p-0 cursor-pointer'
    >
      <FontAwesomeIcon
        icon={on ? (dayPart === 'sun' ? faSun : faMoon) : faSquare}
        className={
          on
            ? dayPart === 'sun'
              ? 'text-yellow-500'
              : 'text-sapphire'
            : 'text-neutral-300'
        }
      />
    </button>
  );
  return (
    <div className='inline-block mx-auto'>
      <div className='grid grid-cols-[1.5rem_repeat(7,1.75rem)] items-center gap-x-1 gap-y-2'>
        <div />
        {days.map((day, i) => (
          <div key={`h-${i}`} className='text-center text-[10px] font-semibold'>
            {day.dayOfWeek?.code?.charAt(0) ?? ''}
          </div>
        ))}
        <div className='text-[10px] font-semibold'>
          {t('pages.sitterProfile.modals.jobSeekingStatusModal.amLabel')}
        </div>
        {days.map((day, i) => (
          <div key={`am-${i}`} className='text-center'>
            {cellButton(!!day.morning, 'sun', day.dayOfWeek?.code ?? '', 'morning', i)}
          </div>
        ))}
        <div className='text-[10px] font-semibold'>
          {t('pages.sitterProfile.modals.jobSeekingStatusModal.pmLabel')}
        </div>
        {days.map((day, i) => (
          <div key={`pm-${i}`} className='text-center'>
            {cellButton(!!day.afternoon, 'sun', day.dayOfWeek?.code ?? '', 'afternoon', i)}
          </div>
        ))}
        <div />
        {days.map((day, i) => (
          <div key={`n-${i}`} className='text-center'>
            {cellButton(!!day.night, 'moon', day.dayOfWeek?.code ?? '', 'night', i)}
          </div>
        ))}
      </div>
    </div>
  );
};

export interface AvailabilityModalSitter {
  id?: number;
  availabilityStatus?: {
    id?: number;
    description?: string;
    interviewTimes?: string;
    jobPreferences?: Array<{ type?: string }>;
    recurringAvailability?: Array<unknown>;
  };
}

export const AvailabilityModal = ({
  isOpen,
  onClose,
  sitter,
  canEdit,
  onSaved,
  lastCalendarUpdateLabel,
}: ModalBase & {
  sitter: AvailabilityModalSitter;
  canEdit: boolean;
  onSaved?: () => void | Promise<void>;
  lastCalendarUpdateLabel?: string;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const initialPrefs = sitter.availabilityStatus?.jobPreferences ?? [];
  const hasType = (type: JobAvailabilityType): boolean =>
    initialPrefs.some((p) => p.type === type);

  const initialRecurring = (): RecurringDay[] => {
    const existing = (sitter.availabilityStatus?.recurringAvailability ?? []) as RecurringDay[];
    if (existing.length === DEFAULT_RECURRING_DAYS.length) return existing.map((d) => ({ ...d }));
    return DEFAULT_RECURRING_DAYS.map((d) => ({ ...d, dayOfWeek: { ...d.dayOfWeek } }));
  };

  const [occasional, setOccasional] = useState(hasType(JobAvailabilityType.occasional));
  const [partTime, setPartTime] = useState(hasType(JobAvailabilityType.partTime));
  const [fullTime, setFullTime] = useState(hasType(JobAvailabilityType.fullTime));
  const [description, setDescription] = useState(
    sitter.availabilityStatus?.description ?? '',
  );
  const [interviewTimes, setInterviewTimes] = useState(
    sitter.availabilityStatus?.interviewTimes ?? '',
  );
  const [recurringAvailability, setRecurringAvailability] =
    useState<RecurringDay[]>(initialRecurring);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setOccasional(hasType(JobAvailabilityType.occasional));
      setPartTime(hasType(JobAvailabilityType.partTime));
      setFullTime(hasType(JobAvailabilityType.fullTime));
      setDescription(sitter.availabilityStatus?.description ?? '');
      setInterviewTimes(sitter.availabilityStatus?.interviewTimes ?? '');
      setRecurringAvailability(initialRecurring());
    }

  }, [isOpen, sitter.id]);

  const toggleRecurring = (index: number, slot: RecurringSlot): void => {
    setRecurringAvailability((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [slot]: !d[slot] } : d)),
    );
  };

  const save = async (): Promise<void> => {
    if (!sitter.id) return;
    setSaving(true);
    const prefs: JobPreference[] = [];
    if (occasional) prefs.push({ type: JobAvailabilityType.occasional });
    if (partTime) prefs.push({ type: JobAvailabilityType.partTime });
    if (fullTime) prefs.push({ type: JobAvailabilityType.fullTime });
    const payload = {
      sitterId: sitter.id,
      jobPreferences: prefs,
      description,
      interviewTimes,
      recurringAvailability,
    };
    try {
      const statusId = sitter.availabilityStatus?.id;
      if (statusId) {
        await AvailabilityStatusService.patch(statusId, { id: statusId, ...payload });
      } else {
        await AvailabilityStatusService.create(payload);
      }
      toast.success(t('pages.sitterProfile.availability.updated'));
      await onSaved?.();
      onClose();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('pages.sitterProfile.modals.availability')}
        className='w-full max-w-2xl'
      >
        {sitter.availabilityStatus?.description ? (
          <p className='mb-3'>{sitter.availabilityStatus.description}</p>
        ) : (
          <p className='mb-3 text-neutral-500'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.noAvailabilityPosted')}
          </p>
        )}
        <p className='text-sm text-neutral-600'>
          {t('pages.sitterProfile.modals.jobSeekingStatusModal.fullScheduleLeading')}{' '}
          <a href={`/app/sitter/${sitter.id}/calendar`} className='underline'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.viewCalendar')}
          </a>
          .
        </p>
      </Modal>
    );
  }

  const descRemaining = STATUS_DESCRIPTION_MAX - description.length;
  const interviewRemaining = STATUS_DESCRIPTION_MAX - interviewTimes.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.jobSeekingStatus')}
      className='w-full max-w-3xl'
    >
      <h3 className='mb-3 font-semibold'>
        {t('pages.sitterProfile.modals.jobSeekingStatusModal.whatTypeHeading')}
      </h3>

      <div className='border border-neutral-200 rounded-lg p-4 mb-4 bg-neutral-50'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <Checkbox
            id='occasional-jobs'
            checked={occasional}
            onChange={(e): void => setOccasional(e.target.checked)}
            label={t('pages.sitterProfile.modals.jobSeekingStatusModal.occasionalJobs')}
          />
          <div>
            <h5 className='mb-1 font-semibold'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.occasionalAvailabilityHeading')}
            </h5>
            <div>
              <i className='fa-regular fa-calendar text-sapphire mr-1' />
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.keepCalendarCurrent')}
            </div>
            {lastCalendarUpdateLabel && (
              <div className='italic text-sm text-neutral-600'>
                {t('pages.sitterProfile.modals.jobSeekingStatusModal.lastUpdated', {
                  label: lastCalendarUpdateLabel,
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='border border-neutral-200 rounded-lg p-4 mb-4 bg-neutral-50'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <Checkbox
              id='part-time-jobs'
              className='mb-3'
              checked={partTime}
              onChange={(e): void => setPartTime(e.target.checked)}
              label={t('pages.sitterProfile.modals.jobSeekingStatusModal.partTimeJobs')}
            />
            <Checkbox
              id='full-time-jobs'
              className='mb-3'
              checked={fullTime}
              onChange={(e): void => setFullTime(e.target.checked)}
              label={t('pages.sitterProfile.modals.jobSeekingStatusModal.fullTimeJobs')}
            />
            <h5 className='font-semibold mb-0'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.moreDetailsHeading')}
            </h5>
            <div className='text-right text-xs italic text-neutral-500 mb-1'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.charactersRemaining', {
                count: descRemaining,
              })}
            </div>
            <textarea
              aria-label={t('pages.sitterProfile.modals.availabilityDescAriaLabel')}
              className='form-control w-full border border-neutral-300 rounded p-2'
              rows={3}
              maxLength={STATUS_DESCRIPTION_MAX}
              value={description}
              onChange={(e): void => setDescription(e.target.value)}
              placeholder={t('pages.sitterProfile.modals.availabilityDescPlaceholder')}
            />
          </div>
          <div>
            <h5 className='font-semibold mb-1'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotHeading')}
            </h5>
            <p className='text-sm mb-2'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotBodyPrefix')}{' '}
              <a href={`/app/sitter/${sitter.id}/calendar`} className='underline'>
                {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotBodyLink')}
              </a>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotBodySuffix')}
            </p>
            <AvailabilityRecurringEditor
              days={recurringAvailability}
              onToggle={toggleRecurring}
            />
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <h5 className='font-semibold mb-0'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.interviewHeading')}
          </h5>
          <div className='text-sm mb-1'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.interviewBody')}
          </div>
          <div className='text-right text-xs italic text-neutral-500 mb-1'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.charactersRemaining', {
              count: interviewRemaining,
            })}
          </div>
          <textarea
            aria-label={t('pages.sitterProfile.modals.interviewTimesAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded p-2'
            rows={3}
            maxLength={STATUS_DESCRIPTION_MAX}
            value={interviewTimes}
            onChange={(e): void => setInterviewTimes(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.interviewTimesPlaceholder')}
          />
        </div>
        <div className='text-center self-center'>
          <div className='mb-3'>
            <b>{t('pages.sitterProfile.modals.jobSeekingStatusModal.takingBreakHeading')}</b>{' '}
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.takingBreakBody')}
          </div>
          <Button
            id='availability-status-modal-save'
            buttonType={ButtonType.primary}
            className='btn-block w-full'
            disabled={saving}
            onClick={save}
          >
            {saving ? (
              <>
                <i className='fa-solid fa-spinner fa-spin mr-2' />{' '}
                {t('pages.sitterProfile.modals.jobSeekingStatusModal.loading')}
              </>
            ) : (
              t('pages.sitterProfile.modals.jobSeekingStatusModal.update')
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// -- Simple Edit Modals (self-view) ----------------------------------------
// Covers the common pattern: one textarea or select + Save button that
// PATCHes the sitter and updates parent state. Each Angular modal it stands
// in for is listed in the heading prop used when opened.

export const TextAreaEditModal = ({
  isOpen,
  onClose,
  title,
  label,
  helperText,
  initialValue,
  maxLength,
  onSave,
}: ModalBase & {
  title: string;
  label: string;
  helperText?: string;
  initialValue: string;
  maxLength?: number;
  onSave: (value: string) => Promise<void>;
}): JSX.Element => {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setError('');
    }
  }, [isOpen, initialValue]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave(value);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <label className='block mb-2 font-semibold text-base'>{label}</label>
      <textarea
        className='w-full border border-neutral-300 rounded p-2'
        rows={6}
        maxLength={maxLength}
        value={value}
        onChange={(e): void => setValue(e.target.value)}
      />
      {helperText && <p className='text-sm text-neutral-600 mt-1'>{helperText}</p>}
      {maxLength && (
        <div className='text-sm text-neutral-600 mt-1'>
          {maxLength - value.length} characters remaining
        </div>
      )}
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};

export const MultiSelectEditModal = ({
  isOpen,
  onClose,
  title,
  options,
  initialSelected,
  onSave,
  allowOther = false,
  otherLabel = 'Other:',
  otherPlaceholder = 'Other',
  initialOther = '',
  columns = 3,
  maxWidth = 'max-w-3xl',
}: ModalBase & {
  title: string;
  options: Array<{ id: number; name: string; description?: string }>;
  initialSelected: number[];
  // The `otherName` arg is set when `allowOther` is true and the user typed a
  // value into the freeform input. Callers who use the preset-only variant
  // can ignore it.
  onSave: (ids: number[], otherName?: string) => Promise<void>;
  allowOther?: boolean;
  otherLabel?: string;
  otherPlaceholder?: string;
  initialOther?: string;
  columns?: 1 | 2 | 3;
  maxWidth?: string;
}): JSX.Element => {
  const [selected, setSelected] = useState<Set<number>>(new Set(initialSelected));
  const [other, setOther] = useState<string>(initialOther);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(initialSelected));
      setOther(initialOther);
      setError('');
    }
  }, [isOpen, initialSelected, initialOther]);

  const toggle = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave([...selected], allowOther ? other.trim() : undefined);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Angular renders options in a flat multi-column grid (col-sm-4/col-sm-6).
  // Recreate that with CSS columns so checkboxes are visible at a glance —
  // no scrolling dropdown.
  const colClass =
    columns === 3
      ? 'sm:columns-2 md:columns-3'
      : columns === 2
        ? 'sm:columns-2'
        : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className={`w-full ${maxWidth}`}
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={`${colClass} gap-x-6`}>
        {options.map((option) => (
          <label
            key={option.id}
            className='flex items-start gap-2 py-1 break-inside-avoid'
          >
            <input
              type='checkbox'
              checked={selected.has(option.id)}
              onChange={(): void => toggle(option.id)}
              className='mt-1 flex-shrink-0'
            />
            <span className='min-w-0'>
              <span className='font-medium'>{option.name}</span>
              {option.description && (
                <span className='text-sm block text-neutral-600'>{option.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {allowOther && (
        <div className='mt-4 flex items-center gap-3'>
          <label htmlFor='other-edit-input' className='font-semibold whitespace-nowrap'>
            {otherLabel}
          </label>
          <input
            id='other-edit-input'
            type='text'
            className='form-control flex-1 border border-neutral-300 rounded px-3 py-2'
            placeholder={otherPlaceholder}
            value={other}
            onChange={(e): void => setOther(e.target.value)}
          />
        </div>
      )}
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};

// -- Other Services Edit Modal --------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-other-services-modal/
// Three rows (pet / senior / household) each with toggles on the left and a
// free-form "experience" textarea on the right. Household opens an
// additional set of specialServices sub-checkboxes when "Set service details"
// is clicked.
const OTHER_EXPERIENCE_MAX = 500;

const Toggle = ({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
}): JSX.Element => (
  <label htmlFor={id} className='inline-flex items-center gap-2 cursor-pointer select-none'>
    <span className='relative inline-block w-10 h-5 align-middle'>
      <input
        id={id}
        type='checkbox'
        className='sr-only peer'
        checked={checked}
        onChange={(e): void => onChange(e.target.checked)}
      />
      <span className='absolute inset-0 bg-neutral-300 rounded-full peer-checked:bg-sapphire transition-colors' />
      <span className='absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full transition-transform peer-checked:translate-x-5' />
    </span>
    {label && <span className='font-medium'>{label}</span>}
  </label>
);

export const OtherServicesEditModal = ({
  isOpen,
  onClose,
  initialWillSitDog,
  initialWillSitCat,
  initialWillSitOtherPet,
  initialWillSitSenior,
  initialWillSitHome,
  initialPetSittingExperience,
  initialSeniorSittingExperience,
  initialHomeSittingExperience,
  initialSpecialServiceIds,
  specialServices,
  onSave,
}: ModalBase & {
  initialWillSitDog?: boolean;
  initialWillSitCat?: boolean;
  initialWillSitOtherPet?: boolean;
  initialWillSitSenior?: boolean;
  initialWillSitHome?: boolean;
  initialPetSittingExperience?: string;
  initialSeniorSittingExperience?: string;
  initialHomeSittingExperience?: string;
  initialSpecialServiceIds: number[];
  specialServices: Array<{ id: number; name: string }>;
  onSave: (patch: {
    willSitDog: boolean;
    willSitCat: boolean;
    willSitOtherPet: boolean;
    willSitSenior: boolean;
    willSitHome: boolean;
    petSittingExperience?: string;
    seniorSittingExperience?: string;
    homeSittingExperience?: string;
    specialServices: Array<{ id: number; name: string }>;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [dog, setDog] = useState(!!initialWillSitDog);
  const [cat, setCat] = useState(!!initialWillSitCat);
  const [otherPet, setOtherPet] = useState(!!initialWillSitOtherPet);
  const [senior, setSenior] = useState(!!initialWillSitSenior);
  const [home, setHome] = useState(!!initialWillSitHome);
  const [petText, setPetText] = useState(initialPetSittingExperience ?? '');
  const [seniorText, setSeniorText] = useState(initialSeniorSittingExperience ?? '');
  const [homeText, setHomeText] = useState(initialHomeSittingExperience ?? '');
  const [serviceIds, setServiceIds] = useState<Set<number>>(
    new Set(initialSpecialServiceIds),
  );
  const [servicesVisible, setServicesVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDog(!!initialWillSitDog);
      setCat(!!initialWillSitCat);
      setOtherPet(!!initialWillSitOtherPet);
      setSenior(!!initialWillSitSenior);
      setHome(!!initialWillSitHome);
      setPetText(initialPetSittingExperience ?? '');
      setSeniorText(initialSeniorSittingExperience ?? '');
      setHomeText(initialHomeSittingExperience ?? '');
      setServiceIds(new Set(initialSpecialServiceIds));
      setServicesVisible(false);
    }
  }, [
    isOpen,
    initialWillSitDog,
    initialWillSitCat,
    initialWillSitOtherPet,
    initialWillSitSenior,
    initialWillSitHome,
    initialPetSittingExperience,
    initialSeniorSittingExperience,
    initialHomeSittingExperience,
    initialSpecialServiceIds,
  ]);

  const hasPetcare = dog || cat || otherPet;

  const toggleService = (id: number): void => {
    setServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const selectedServices = specialServices.filter((s) => serviceIds.has(s.id));
      await onSave({
        willSitDog: dog,
        willSitCat: cat,
        willSitOtherPet: otherPet,
        willSitSenior: senior,
        willSitHome: home,
        petSittingExperience: petText || undefined,
        seniorSittingExperience: seniorText || undefined,
        homeSittingExperience: homeText || undefined,
        specialServices: selectedServices,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = (text: string): number => OTHER_EXPERIENCE_MAX - text.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editOtherServices')}
      className='w-full max-w-3xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-sitter-petcare'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-6'>
        <div className='sm:col-span-7'>
          <h3 className='font-semibold text-lg mb-2'>Pet Care</h3>
          <p className='mb-2'>
            Willing to provide pet care for any of the following&nbsp;animals?
          </p>
          <div className='space-y-2'>
            <Toggle id='will-sit-dog' checked={dog} onChange={setDog} label='Dogs' />
            <div />
            <Toggle id='will-sit-cat' checked={cat} onChange={setCat} label='Cats' />
            <div />
            <Toggle
              id='will-sit-other-pet'
              checked={otherPet}
              onChange={setOtherPet}
              label='Other pets'
            />
          </div>
        </div>
        <div className='sm:col-span-5'>
          <textarea
            aria-label={t('pages.sitterProfile.modals.petExperienceAriaLabel')}
            id='pet-sitting-experience'
            className='form-control w-full border border-neutral-300 rounded p-2 disabled:bg-neutral-100'
            rows={6}
            disabled={!hasPetcare}
            maxLength={OTHER_EXPERIENCE_MAX}
            value={petText}
            onChange={(e): void => setPetText(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.petExperiencePlaceholder')}
          />
          <i className='text-xs text-neutral-500 block text-right mt-1 not-italic'>
            {remaining(petText)} characters remaining
          </i>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-6'>
        <div className='sm:col-span-7'>
          <h3 className='font-semibold text-lg mb-2'>Senior Care</h3>
          <p className='mb-2'>Willing to provide non-medical senior care?</p>
          <div className='flex items-center gap-3'>
            <Toggle id='will-sit-senior' checked={senior} onChange={setSenior} />
            <span>{senior ? 'Yes' : 'No'}</span>
          </div>
        </div>
        <div className='sm:col-span-5'>
          <textarea
            aria-label={t('pages.sitterProfile.modals.seniorExperienceAriaLabel')}
            id='senior-sitting-experience'
            className='form-control w-full border border-neutral-300 rounded p-2 disabled:bg-neutral-100'
            rows={6}
            disabled={!senior}
            maxLength={OTHER_EXPERIENCE_MAX}
            value={seniorText}
            onChange={(e): void => setSeniorText(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.seniorExperiencePlaceholder')}
          />
          <i className='text-xs text-neutral-500 block text-right mt-1 not-italic'>
            {remaining(seniorText)} characters remaining
          </i>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4'>
        <div className='sm:col-span-7'>
          <h3 className='font-semibold text-lg mb-2'>Household Services</h3>
          <p className='mb-2'>
            Willing to provide household services that don&apos;t involve children,
            pets, or senior&nbsp;care?
          </p>
          <div className='flex items-center gap-3 flex-wrap'>
            <Toggle id='will-sit-home' checked={home} onChange={setHome} />
            <span>{home ? 'Yes' : 'No'}</span>
            {home && (
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => setServicesVisible((v) => !v)}
              >
                {servicesVisible ? 'Hide' : 'Set'} service details
              </Button>
            )}
          </div>
          {home && servicesVisible && (
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3'>
              {specialServices.map((service) => (
                <label
                  key={service.id}
                  className='flex items-start gap-2 cursor-pointer'
                >
                  <input
                    id={`edit-service-${service.id}`}
                    type='checkbox'
                    className='mt-1'
                    checked={serviceIds.has(service.id)}
                    onChange={(): void => toggleService(service.id)}
                  />
                  <span>{service.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className='sm:col-span-5'>
          <textarea
            aria-label={t('pages.sitterProfile.modals.householdExperienceAriaLabel')}
            id='home-sitting-experience'
            className='form-control w-full border border-neutral-300 rounded p-2 disabled:bg-neutral-100'
            rows={6}
            disabled={!home}
            maxLength={OTHER_EXPERIENCE_MAX}
            value={homeText}
            onChange={(e): void => setHomeText(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.householdExperiencePlaceholder')}
          />
          <i className='text-xs text-neutral-500 block text-right mt-1 not-italic'>
            {remaining(homeText)} characters remaining
          </i>
        </div>
      </div>

    </Modal>
  );
};

// -- Rates Edit Modal ------------------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-rates-modal/
const pluralizeKidsLabel = (n: number): string =>
  n === 1 ? '1 child, senior, or pet' : `${n} children, seniors, or pets`;

const MINIMUM_HOUR_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'None' },
  { value: 2, label: '2 hours' },
  { value: 3, label: '3 hours' },
  { value: 4, label: '4 hours' },
  { value: 5, label: '5 hours' },
  { value: 6, label: '6 hours' },
  { value: 8, label: '8 hours' },
];

export const RatesEditModal = ({
  isOpen,
  onClose,
  initialRates,
  initialMaxKids,
  initialMinimumHours,
  initialAcceptsFixedRates,
  initialWillSitSingleFamily,
  suggestedRate,
  onSave,
}: ModalBase & {
  initialRates: Array<{ minimumRate?: number; maximumRate?: number }>;
  initialMaxKids?: number;
  initialMinimumHours?: number;
  initialAcceptsFixedRates?: boolean;
  initialWillSitSingleFamily?: boolean;
  suggestedRate?: { rate?: number; defaultRate?: boolean };
  onSave: (patch: {
    rates: Array<{ minimumRate: number; maximumRate: number }>;
    maxKids?: number;
    minimumHours?: number;
    acceptsFixedRates?: boolean;
    willSitSingleFamily?: boolean;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [rates, setRates] = useState(() =>
    [0, 1, 2, 3].map((i) => ({
      minimumRate: String(initialRates[i]?.minimumRate ?? ''),
      maximumRate: String(initialRates[i]?.maximumRate ?? ''),
    })),
  );
  const [maxKids, setMaxKids] = useState(String(initialMaxKids ?? ''));
  const [minimumHours, setMinimumHours] = useState(String(initialMinimumHours ?? ''));
  const [acceptsFixedRates, setAcceptsFixedRates] = useState(!!initialAcceptsFixedRates);
  const [willSitSingleFamily, setWillSitSingleFamily] = useState(!!initialWillSitSingleFamily);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRates(
        [0, 1, 2, 3].map((i) => ({
          minimumRate: String(initialRates[i]?.minimumRate ?? ''),
          maximumRate: String(initialRates[i]?.maximumRate ?? ''),
        })),
      );
      setMaxKids(String(initialMaxKids ?? ''));
      setMinimumHours(String(initialMinimumHours ?? ''));
      setAcceptsFixedRates(!!initialAcceptsFixedRates);
      setWillSitSingleFamily(!!initialWillSitSingleFamily);
      setError('');
    }
  }, [isOpen, initialRates, initialMaxKids, initialMinimumHours, initialAcceptsFixedRates, initialWillSitSingleFamily]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const cleaned = rates
        .map((r) => ({
          minimumRate: Number(r.minimumRate) || 0,
          maximumRate: Number(r.maximumRate) || Number(r.minimumRate) || 0,
        }))
        .filter((r) => r.minimumRate > 0);
      if (cleaned[0] && cleaned[0].minimumRate < 8) {
        setError('Minimum rate must be at least $8.');
        setSubmitting(false);
        return;
      }
      await onSave({
        rates: cleaned,
        maxKids: maxKids ? Number(maxKids) : undefined,
        minimumHours: minimumHours ? Number(minimumHours) : undefined,
        acceptsFixedRates,
        willSitSingleFamily,
      });
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editRates')}
      className='w-full max-w-3xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-sitter-rates'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
        <div>
          <h4 className='mb-1 font-semibold'>
            Rates by number of children, seniors, or pets:
          </h4>
          {suggestedRate?.rate != null && suggestedRate.rate > 0 && (
            <div className='mb-2'>
              {suggestedRate.defaultRate
                ? `On average, caregivers charge $${suggestedRate.rate}/hr`
                : `Caregivers in your area are charging $${suggestedRate.rate}/hr`}
            </div>
          )}
          <div className='mb-3 text-sm'>
            Based on our data, caregivers often start out with lower rates until they can
            build up their reviews and repeat families.
          </div>
          <div className='space-y-2'>
            {rates.map((r, i) => (
              <div key={i} className='flex items-center gap-2'>
                <span className='flex-1 text-sm'>{pluralizeKidsLabel(i + 1)}</span>
                <div className='flex items-center border border-neutral-300 rounded w-24 flex-shrink-0'>
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id={`min-rate-${i + 1}`}
                    aria-label={`Minimum rate for ${i + 1}`}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={2}
                    className='w-full px-1 py-1 text-sm min-w-0'
                    value={r.minimumRate}
                    onChange={(e): void =>
                      setRates((prev) =>
                        prev.map((row, idx) =>
                          idx === i ? { ...row, minimumRate: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </div>
                <span className='text-center flex-shrink-0'>-</span>
                <div className='flex items-center border border-neutral-300 rounded w-24 flex-shrink-0'>
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id={`max-rate-${i + 1}`}
                    aria-label={`Maximum rate for ${i + 1}`}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={2}
                    className='w-full px-1 py-1 text-sm min-w-0'
                    value={r.maximumRate}
                    onChange={(e): void =>
                      setRates((prev) =>
                        prev.map((row, idx) =>
                          idx === i ? { ...row, maximumRate: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className='mb-1 font-semibold'>Children, seniors, or pets maximum:</h4>
          <select
            id='max-num-children'
            aria-label={t('pages.sitterProfile.modals.maxChildrenAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-4'
            value={maxKids}
            onChange={(e): void => setMaxKids(e.target.value)}
          >
            <option value=''>Select</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>
                {pluralizeKidsLabel(n)}
              </option>
            ))}
          </select>

          <h4 className='mb-1 font-semibold'>Minimum hours per job</h4>
          <select
            id='minimum-hours'
            aria-label={t('pages.sitterProfile.modals.minHoursAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
            value={minimumHours}
            onChange={(e): void => setMinimumHours(e.target.value)}
          >
            {MINIMUM_HOUR_OPTIONS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>

          <h4 className='mb-2 font-semibold'>
            Considerations for part-time or full-time jobs:
          </h4>
          <label className='flex items-start gap-2 mb-2 text-sm'>
            <input
              id='flat-rates-checkbox'
              type='checkbox'
              checked={acceptsFixedRates}
              onChange={(e): void => setAcceptsFixedRates(e.target.checked)}
            />
            Open to flat weekly/monthly rates
          </label>
          <label className='flex items-start gap-2 text-sm'>
            <input
              id='single-family-checkbox'
              type='checkbox'
              checked={willSitSingleFamily}
              onChange={(e): void => setWillSitSingleFamily(e.target.checked)}
            />
            Willing to work with one family exclusively, given a weekly hours commitment
          </label>
        </div>
      </div>

      {error && <p className='text-red-600 text-sm mt-3'>{error}</p>}
    </Modal>
  );
};

// -- Location Edit Modal ---------------------------------------------------
export const LocationEditModal = ({
  isOpen,
  onClose,
  initialAddress,
  initialWorksWithin,
  onSave,
}: ModalBase & {
  initialAddress?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    neighborhood?: string;
  };
  initialWorksWithin?: number;
  onSave: (patch: {
    address: {
      street?: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      neighborhood?: string;
    };
    worksWithin?: number;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [street, setStreet] = useState(initialAddress?.street ?? '');
  const [street2, setStreet2] = useState(initialAddress?.street2 ?? '');
  const [city, setCity] = useState(initialAddress?.city ?? '');
  const [state, setState] = useState(initialAddress?.state ?? '');
  const [zip, setZip] = useState(initialAddress?.zip ?? '');
  const [neighborhood, setNeighborhood] = useState(initialAddress?.neighborhood ?? '');
  const [worksWithin, setWorksWithin] = useState(String(initialWorksWithin ?? ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStreet(initialAddress?.street ?? '');
      setStreet2(initialAddress?.street2 ?? '');
      setCity(initialAddress?.city ?? '');
      setState(initialAddress?.state ?? '');
      setZip(initialAddress?.zip ?? '');
      setNeighborhood(initialAddress?.neighborhood ?? '');
      setWorksWithin(String(initialWorksWithin ?? ''));
      setError('');
    }
  }, [isOpen, initialAddress, initialWorksWithin]);

  const submit = async (): Promise<void> => {
    if (!city.trim() || !state.trim() || !zip.trim()) {
      setError('City, state, and ZIP are required.');
      return;
    }
    setSubmitting(true);
    try {
      // Angular sends `angular.copy(sitter.address)` — the full original
      // address with the user's edits overlaid — so latitude/longitude and
      // any other fields the server depends on survive the patch. Mirror that
      // here, otherwise PATCH may drop required positioning data.
      await onSave({
        address: {
          ...(initialAddress ?? {}),
          street: street || undefined,
          street2: street2 || undefined,
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
          neighborhood: neighborhood || undefined,
        },
        worksWithin: worksWithin ? Number(worksWithin) : undefined,
      });
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editLocation')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <p className='mb-1 font-semibold text-base'>Add your street address:</p>
      <p className='text-sm mb-4 text-neutral-600'>
        To calculate distance. Your address will NOT be public.
      </p>
      <div className='grid grid-cols-12 gap-3 mb-3'>
        <div className='col-span-7'>
          <label htmlFor='sitter-street' className='block text-base font-semibold mb-1'>
            Street
          </label>
          <input
            id='sitter-street'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderStreet')}
            value={street}
            onChange={(e): void => setStreet(e.target.value)}
          />
        </div>
        <div className='col-span-5'>
          <label htmlFor='sitter-apt' className='block text-base font-semibold mb-1'>
            Apt #
          </label>
          <input
            id='sitter-apt'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderApt')}
            value={street2}
            onChange={(e): void => setStreet2(e.target.value)}
          />
        </div>
      </div>
      <div className='grid grid-cols-12 gap-3 mb-3'>
        <div className='col-span-7'>
          <label htmlFor='sitter-city' className='block text-base font-semibold mb-1'>
            City
          </label>
          <input
            id='sitter-city'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderCity')}
            value={city}
            onChange={(e): void => setCity(e.target.value)}
          />
        </div>
        <div className='col-span-5'>
          <label htmlFor='sitter-state' className='block text-base font-semibold mb-1'>
            State
          </label>
          <input
            id='sitter-state'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderState')}
            maxLength={2}
            value={state}
            onChange={(e): void => setState(e.target.value)}
          />
        </div>
      </div>
      <div className='grid grid-cols-12 gap-3 mb-3'>
        <div className='col-span-4'>
          <label htmlFor='sitter-zip' className='block text-base font-semibold mb-1'>
            Zip Code
          </label>
          <input
            id='sitter-zip'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderZip')}
            maxLength={5}
            value={zip}
            onChange={(e): void => setZip(e.target.value)}
          />
        </div>
        <div className='col-span-8'>
          <label
            htmlFor='sitter-neighborhood'
            className='block text-base font-semibold mb-1'
          >
            Neighborhood
          </label>
          <input
            id='sitter-neighborhood'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderNeighborhood')}
            value={neighborhood}
            onChange={(e): void => setNeighborhood(e.target.value)}
          />
        </div>
      </div>
      <label htmlFor='works-within-distance' className='block font-semibold mb-1 mt-5 text-base'>
        Willing to work within:
      </label>
      <select
        id='works-within-distance'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
        value={worksWithin}
        onChange={(e): void => setWorksWithin(e.target.value)}
      >
        <option value=''>Any distance</option>
        <option value='3'>3 miles</option>
        <option value='5'>5 miles</option>
        <option value='10'>10 miles</option>
        <option value='15'>15 miles</option>
        <option value='25'>25 miles</option>
        <option value='50'>50 miles</option>
      </select>
      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};

// -- Age Experience Edit Modal ---------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-age-experience-modal/
type AgeKey = 'infant' | 'toddler' | 'school';

const AGE_LABEL: Record<AgeKey, { title: string; detail: string; suffix: string }> = {
  infant: {
    title: 'Infants',
    detail: '(under 1 year old)',
    suffix: 'infants',
  },
  toddler: {
    title: 'Toddlers',
    detail: '(1-3 years old)',
    suffix: 'toddlers',
  },
  school: {
    title: 'School age',
    detail: '(4+ years old)',
    suffix: 'school age children',
  },
};

const formatExperienceLabel = (suffix: string, label: string): string => {
  if (label.indexOf('Not willing') > -1) {
    return `${label} ${suffix}`;
  }
  return label;
};

export const AgeExperienceEditModal = ({
  isOpen,
  onClose,
  ageExperiences,
  initialExperiences,
  onSave,
}: ModalBase & {
  ageExperiences: Array<{ id: number; label: string }>;
  initialExperiences?: {
    infant?: { id?: number; label?: string };
    toddler?: { id?: number; label?: string };
    school?: { id?: number; label?: string };
  };
  onSave: (experiences: {
    infant?: { id: number };
    toddler?: { id: number };
    school?: { id: number };
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [infantId, setInfantId] = useState<number | ''>(
    initialExperiences?.infant?.id ?? '',
  );
  const [toddlerId, setToddlerId] = useState<number | ''>(
    initialExperiences?.toddler?.id ?? '',
  );
  const [schoolId, setSchoolId] = useState<number | ''>(
    initialExperiences?.school?.id ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInfantId(initialExperiences?.infant?.id ?? '');
      setToddlerId(initialExperiences?.toddler?.id ?? '');
      setSchoolId(initialExperiences?.school?.id ?? '');
    }
  }, [isOpen, initialExperiences]);

  // Angular patches the sitter with whatever the user has selected — partial
  // selections are allowed. The earlier React guard `if (!infantId || ...) return`
  // silently aborted (no toast, no close) when any of the three was unset or
  // its id was 0, which is why Save did nothing visibly. Match Angular: pass
  // only the chosen groups, no all-or-nothing requirement.
  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave({
        infant: infantId !== '' ? { id: Number(infantId) } : undefined,
        toddler: toddlerId !== '' ? { id: Number(toddlerId) } : undefined,
        school: schoolId !== '' ? { id: Number(schoolId) } : undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const renderGroup = (
    key: AgeKey,
    value: number | '',
    setter: (v: number | '') => void,
  ): JSX.Element => (
    <div className='mb-4'>
      <div className='font-semibold'>
        {AGE_LABEL[key].title} <span className='font-normal'>{AGE_LABEL[key].detail}</span>
      </div>
      <select
        id={`edit-sitter-exp-${key === 'school' ? 'school-age' : key}`}
        aria-label={`${AGE_LABEL[key].title} experience`}
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mt-1'
        value={value}
        onChange={(e): void => setter(e.target.value ? Number(e.target.value) : '')}
      >
        <option value=''>Select</option>
        {ageExperiences.map((exp) => (
          <option key={exp.id} value={exp.id}>
            {formatExperienceLabel(AGE_LABEL[key].suffix, exp.label)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editAgeExperience')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-age-experience'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <h4 className='mb-3'>
        What are the ages you are willing to babysit? How many years of experience do you have?
      </h4>
      {renderGroup('infant', infantId, setInfantId)}
      {renderGroup('toddler', toddlerId, setToddlerId)}
      {renderGroup('school', schoolId, setSchoolId)}
    </Modal>
  );
};

// -- Education Edit Modal --------------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-education-modal/
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MIN_GRADUATION_YEAR = 1900;
const maxGraduationYear = (): number => new Date().getFullYear() + 10;

const validateGraduationYear = (year: string): boolean => {
  if (!year) return true;
  if (!/^\d{4}$/.test(year)) return false;
  const n = Number(year);
  return n >= MIN_GRADUATION_YEAR && n <= maxGraduationYear();
};

// Async-loaded typeahead input. Mirrors Angular's <sitter-college-select>
// (ui-select with `refresh="getEntities($select.search)"`) — the user types,
// we hit the search endpoint, and pick from suggestions. Falls back to
// free-typed entry if the user types something not in the suggestion list.
const TypeaheadSelect = ({
  id,
  placeholder,
  initialValue,
  onSelect,
  fetchSuggestions,
}: {
  id?: string;
  placeholder: string;
  initialValue?: { id?: number; name?: string };
  onSelect: (value?: { id?: number; name: string }) => void;
  fetchSuggestions: (q: string) => Promise<Array<{ id: number; name: string }>>;
}): JSX.Element => {
  const [query, setQuery] = useState(initialValue?.name ?? '');
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{ id: number; name: string }>>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQuery(initialValue?.name ?? '');
  }, [initialValue?.id, initialValue?.name]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query.trim())
        .then((items) => setSuggestions(items.slice(0, 12)))
        .catch(() => setSuggestions([]));
    }, 250);
    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchSuggestions]);

  const choose = (item: { id: number; name: string }): void => {
    setQuery(item.name);
    setOpen(false);
    onSelect(item);
  };

  return (
    <div className='relative w-full'>
      <input
        id={id}
        type='text'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2'
        placeholder={placeholder}
        value={query}
        onChange={(e): void => {
          setQuery(e.target.value);
          setOpen(true);
          onSelect(undefined);
        }}
        onFocus={(): void => setOpen(true)}
        onBlur={(): void => {
          setTimeout(() => setOpen(false), 150);
        }}
      />
      {open && suggestions.length > 0 && (
        <ul className='absolute left-0 right-0 z-10 bg-white border border-neutral-300 rounded mt-1 max-h-60 overflow-auto shadow'>
          {suggestions.map((item) => (
            <li key={item.id}>
              <button
                type='button'
                className='block w-full text-left px-3 py-2 hover:bg-neutral-100'
                onMouseDown={(): void => choose(item)}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const EducationEditModal = ({
  isOpen,
  onClose,
  initialEducation,
  onSave,
  onDelete,
}: ModalBase & {
  initialEducation?: {
    id?: number;
    college?: { id?: number; name?: string };
    areaOfStudy?: { id?: number; name?: string };
    graduation?: string;
  };
  onSave: (data: {
    id?: number;
    college: { id?: number; name: string };
    areaOfStudy?: { id?: number; name: string };
    graduationMonth?: string;
    graduationYear?: string;
  }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const initialYear = initialEducation?.graduation
    ? new Date(initialEducation.graduation).getFullYear().toString()
    : '';
  const initialMonth = initialEducation?.graduation
    ? MONTHS_SHORT[new Date(initialEducation.graduation).getMonth()]
    : '';

  const [college, setCollege] = useState<{ id?: number; name: string } | undefined>(
    initialEducation?.college?.name
      ? { id: initialEducation.college.id, name: initialEducation.college.name }
      : undefined,
  );
  const [areaOfStudy, setAreaOfStudy] = useState<
    { id?: number; name: string } | undefined
  >(
    initialEducation?.areaOfStudy?.name
      ? { id: initialEducation.areaOfStudy.id, name: initialEducation.areaOfStudy.name }
      : undefined,
  );
  const [month, setMonth] = useState(initialMonth);
  const [year, setYear] = useState(initialYear);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCollege(
        initialEducation?.college?.name
          ? { id: initialEducation.college.id, name: initialEducation.college.name }
          : undefined,
      );
      setAreaOfStudy(
        initialEducation?.areaOfStudy?.name
          ? {
              id: initialEducation.areaOfStudy.id,
              name: initialEducation.areaOfStudy.name,
            }
          : undefined,
      );
      setMonth(initialMonth);
      setYear(initialYear);
    }
  }, [isOpen, initialEducation, initialMonth, initialYear]);

  const yearValid = validateGraduationYear(year);

  const escapeFilterValue = (s: string): string => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const searchColleges = async (q: string): Promise<Array<{ id: number; name: string }>> => {
    const safe = escapeFilterValue(q);
    const filter = `name.containsIgnoreCase("${safe}").or(alias.containsIgnoreCase("${safe}"))`;
    const resp = await CollegeService.getByFilter({ params: { q: filter, limit: 12 } });
    return ((resp.data?.data ?? []) as Array<{ id: number; name: string }>).filter(
      (c) => !!c.name,
    );
  };

  const searchStudyAreas = async (
    q: string,
  ): Promise<Array<{ id: number; name: string }>> => {
    const resp = await CollegeAreaOfStudyService.getByFilter({
      params: { name: q, limit: 12 },
    });
    return ((resp.data?.data ?? []) as Array<{ id: number; name: string }>).filter(
      (s) => !!s.name,
    );
  };

  const submit = async (): Promise<void> => {
    if (!college?.id) return;
    if (!yearValid) return;
    setSubmitting(true);
    try {
      await onSave({
        id: initialEducation?.id,
        college,
        areaOfStudy,
        graduationMonth: month || undefined,
        graduationYear: year || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async (): Promise<void> => {
    if (!initialEducation?.id) return;
    setSubmitting(true);
    try {
      await onDelete(initialEducation.id);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editEducation')}
      className='w-full max-w-2xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          {initialEducation?.id && (
            <Button
              id='delete-sitter-education'
              buttonType={ButtonType.secondary}
              disabled={submitting}
              onClick={clear}
            >
              Clear
            </Button>
          )}
          <Button
            id='save-sitter-education'
            buttonType={ButtonType.primary}
            disabled={!college?.id || !yearValid || submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-12 gap-3 mb-3 items-center'>
        <label className='col-span-4 font-semibold text-base'>College (most recent):</label>
        <div className='col-span-8'>
          <TypeaheadSelect
            id='input-college'
            placeholder={t('pages.sitterProfile.modals.placeholderCollege')}
            initialValue={college}
            onSelect={setCollege}
            fetchSuggestions={searchColleges}
          />
        </div>
      </div>

      <div className='grid grid-cols-12 gap-3 mb-3 items-center'>
        <label className='col-span-4 font-semibold text-base'>Area of study:</label>
        <div className='col-span-8'>
          <TypeaheadSelect
            id='input-study-area'
            placeholder={t('pages.sitterProfile.modals.placeholderMajor')}
            initialValue={areaOfStudy}
            onSelect={setAreaOfStudy}
            fetchSuggestions={searchStudyAreas}
          />
        </div>
      </div>

      <div className='grid grid-cols-12 gap-3 mb-1 items-center'>
        <label htmlFor='select-graduation-month' className='col-span-4 text-base'>
          Graduation date:
        </label>
        <select
          id='select-graduation-month'
          aria-label={t('pages.sitterProfile.modals.expectedGradMonthAriaLabel')}
          className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
          value={month}
          onChange={(e): void => setMonth(e.target.value)}
        >
          <option value=''>Month</option>
          {MONTHS_SHORT.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <input
          id='input-graduation-year'
          aria-label={t('pages.sitterProfile.modals.expectedGradYearAriaLabel')}
          type='tel'
          placeholder={t('pages.sitterProfile.modals.placeholderYear')}
          className={`col-span-4 form-control border rounded px-3 py-2 ${
            yearValid ? 'border-neutral-300' : 'border-red-500'
          }`}
          value={year}
          onChange={(e): void => setYear(e.target.value)}
        />
      </div>
      {yearValid ? (
        <p className='text-sm text-right mb-3'>
          If you&apos;re still in school, enter your expected graduation date.
        </p>
      ) : (
        <p className='text-red-600 text-sm text-right mb-3'>
          Enter a valid year between {MIN_GRADUATION_YEAR} and {maxGraduationYear()}
        </p>
      )}
    </Modal>
  );
};

// -- Certifications Edit Modal ---------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-certifications-modal/
export const CertificationsEditModal = ({
  isOpen,
  onClose,
  certifications,
  initialSelected,
  onSave,
}: ModalBase & {
  certifications: Array<{ id: number; name: string }>;
  initialSelected: Array<{ id: number; name: string }>;
  onSave: (selected: Array<{ id: number; name: string }>) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [selected, setSelected] = useState<Record<string, { id: number; name: string }>>(
    () => Object.fromEntries(initialSelected.map((c) => [c.name, c])),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(Object.fromEntries(initialSelected.map((c) => [c.name, c])));
    }
  }, [isOpen, initialSelected]);

  const toggle = (cert: { id: number; name: string }): void => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[cert.name]) delete next[cert.name];
      else next[cert.name] = cert;
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave(Object.values(selected));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const col1 = certifications.slice(0, 5);
  const col2 = certifications.slice(5, 10);

  const renderCol = (list: Array<{ id: number; name: string }>): JSX.Element => (
    <div className='space-y-2'>
      {list.map((cert) => (
        <label key={cert.id} className='flex items-start gap-2 cursor-pointer'>
          <input
            id={`certification-${cert.name}`}
            type='checkbox'
            checked={!!selected[cert.name]}
            onChange={(): void => toggle(cert)}
            className='mt-1'
          />
          <span>{cert.name}</span>
        </label>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editCertifications')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-certifications'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3 text-base'>
        {renderCol(col1)}
        {renderCol(col2)}
      </div>
    </Modal>
  );
};
